import React, { useMemo, useState } from "react";
import {
    AuditEntry,
    AuditTrailProps,
} from "../../../../types/cdd-records.types";
import {
    formatDateShort,
    formatDateTime,
} from "../../../../utils/dateFormatters";
import { actionLabels, getColorClasses } from "./auditTrailConfig";

/** Convert raw technical notes into human-readable text */
const humanizeNotes = (notes?: string): string | null => {
  if (!notes) return null;
  const flagMap: Record<string, string> = {
    OPS_HAS_IDENTIFIED_ALL_PRINCIPALS: "All principals have been identified",
    OPS_KYC_COMPLETE: "KYC verification complete",
    OPS_PEP_SCREENING_COMPLETE: "PEP screening complete",
    OPS_SANCTIONS_SCREENING_COMPLETE: "Sanctions screening complete",
    OPS_DOCUMENT_VERIFIED: "Document verified",
    OPS_RISK_ASSESSED: "Risk assessment completed",
    OPS_CDD_COMPLETE: "CDD process completed",
  };
  const flagMatch = notes.match(/\[FLAG SET\]\s*(\S+)/);
  if (flagMatch) {
    return flagMap[flagMatch[1]] || flagMatch[1].replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }
  return notes.replace(/Entity ID:\s*\S+/g, "").trim() || null;
};

/** Shorten entity IDs / UUIDs for display */
const shortenId = (id: string): string => {
  if (!id || id.length < 16) return id;
  // UUID pattern
  if (/^[0-9a-f]{8}-/.test(id)) return id.slice(0, 8) + "…";
  // Dotted ID pattern
  if (id.includes(".") && id.length > 20) return id.slice(0, 8) + "…" + id.slice(-6);
  return id;
};

/** Make field names human-readable */
const humanizeField = (field?: string): string | null => {
  if (!field) return null;
  const fieldMap: Record<string, string> = {
    status: "Status",
    riskLevel: "Risk Level",
    verificationStatus: "Verification Status",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    dateOfBirth: "Date of Birth",
    entityType: "Entity Type",
    abn: "ABN",
    acn: "ACN",
  };
  return fieldMap[field] || field.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()).trim();
};

/** Build a concise human-readable summary for a timeline entry */
const buildSummary = (entry: AuditEntry): string => {
  const entity = entry.entityType === "CDD" ? "CDD Record" : entry.entityType;
  const field = humanizeField(entry.field);
  const note = humanizeNotes(entry.notes);

  // If there's a meaningful field change, describe it
  if (field && entry.newValue) {
    return `${field} changed to "${entry.newValue}"`;
  }
  if (field) {
    return `${field} on ${entity}`;
  }
  if (note) {
    return note;
  }
  return entity;
};

/** Format userName — replace UUIDs with "System" */
const formatUser = (name: string): string => {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(name)) return "System";
  return name;
};

export const AuditTrail: React.FC<AuditTrailProps> = ({
  auditTrail,
  onExport,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterUser, setFilterUser] = useState<string>("all");
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"timeline" | "table">("timeline");

  // Get unique users for filter
  const uniqueUsers = useMemo(() => {
    return Array.from(new Set(auditTrail.entries.map((e) => e.userName)));
  }, [auditTrail.entries]);

  // Get unique actions for filter
  const uniqueActions = useMemo(() => {
    return Array.from(new Set(auditTrail.entries.map((e) => e.action)));
  }, [auditTrail.entries]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    return auditTrail.entries.filter((entry) => {
      const matchesSearch =
        searchTerm === "" ||
        entry.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.entityType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.field?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAction =
        filterAction === "all" || entry.action === filterAction;
      const matchesUser = filterUser === "all" || entry.userName === filterUser;
      return matchesSearch && matchesAction && matchesUser;
    });
  }, [auditTrail.entries, searchTerm, filterAction, filterUser]);

  // Group entries by date for timeline view
  const groupedByDate = useMemo(() => {
    const groups: Record<string, AuditEntry[]> = {};
    filteredEntries.forEach((entry) => {
      const date = formatDateShort(entry.timestamp);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      {/* Header with gradient */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">Audit Trail</h3>
            <p className="text-sm text-slate-300 mt-1">
              Complete history of all changes and actions
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-white/10 rounded-lg p-1 backdrop-blur-sm">
              <button
                onClick={() => setViewMode("timeline")}
                className={`px-3 py-1.5 text-sm font-medium rounded ${
                  viewMode === "timeline"
                    ? "bg-white/20 text-white shadow-sm"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1.5 text-sm font-medium rounded ${
                  viewMode === "table"
                    ? "bg-white/20 text-white shadow-sm"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                Table
              </button>
            </div>
            {onExport && (
              <button
                onClick={onExport}
                className="px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-all backdrop-blur-sm flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats - Vibrant Gradient Cards */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-b border-slate-200/60">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Events - Blue to Indigo */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl p-3 sm:p-5 border border-blue-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {auditTrail.entries.length}
                  </p>
                  <p className="text-sm font-semibold text-blue-100 mt-1">
                    Total Events
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Creates - Emerald to Teal */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-3 sm:p-5 border border-emerald-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {
                      auditTrail.entries.filter(
                        (e) => e.action === "create" || e.action === "approve",
                      ).length
                    }
                  </p>
                  <p className="text-sm font-semibold text-emerald-100 mt-1">
                    Created/Approved
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Updates - Purple to Pink */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 rounded-2xl p-3 sm:p-5 border border-purple-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {
                      auditTrail.entries.filter(
                        (e) => e.action === "update" || e.action === "review",
                      ).length
                    }
                  </p>
                  <p className="text-sm font-semibold text-purple-100 mt-1">
                    Updates/Reviews
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Unique Users - Amber to Orange */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 rounded-2xl p-3 sm:p-5 border border-amber-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {uniqueUsers.length}
                  </p>
                  <p className="text-sm font-semibold text-amber-100 mt-1">
                    Unique Users
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-b border-slate-200/60">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search audit entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="min-w-[150px]">
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Actions</option>
              {uniqueActions.map((action) => (
                <option key={action} value={action}>
                  {actionLabels[action]?.label || action}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[150px]">
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Users</option>
              {uniqueUsers.map((user) => (
                <option key={user} value={user}>
                  {user}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="px-6 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200/60 flex items-center justify-between text-sm text-slate-600">
        <span>
          Showing {filteredEntries.length} of {auditTrail.entries.length}{" "}
          entries
        </span>
        <span>
          Created: {formatDateShort(auditTrail.createdDate)} by{" "}
          {auditTrail.createdBy}
          {auditTrail.lastModifiedDate && (
            <>
              {" "}
              • Last modified: {formatDateShort(
                auditTrail.lastModifiedDate,
              )} by {auditTrail.lastModifiedBy}
            </>
          )}
        </span>
      </div>

      {/* Content */}
      <div className="p-6">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No audit entries found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : viewMode === "timeline" ? (
          /* Timeline View */
          <div className="space-y-6">
            {Object.entries(groupedByDate).map(([date, entries]) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-semibold text-gray-900">
                    {date}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({entries.length} entries)
                  </span>
                </div>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                  <div className="space-y-4">
                    {entries.map((entry) => {
                      const actionConfig =
                        actionLabels[entry.action] || actionLabels["view"];
                      const colorClasses = getColorClasses(actionConfig.color);
                      const isExpanded = expandedEntry === entry.id;

                      return (
                        <div key={entry.id} className="relative pl-10">
                          {/* Timeline dot */}
                          <div
                            className={`absolute left-2 w-5 h-5 rounded-full ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center border-2 ${colorClasses.border}`}
                          >
                            {actionConfig.icon}
                          </div>

                          <div
                            className={`p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors ${isExpanded ? "bg-gray-50" : ""}`}
                            onClick={() =>
                              setExpandedEntry(isExpanded ? null : entry.id)
                            }
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-sm font-medium ${colorClasses.text}`}
                                >
                                  {actionConfig.label}
                                </span>
                                <span className="text-sm text-gray-600">
                                  {buildSummary(entry)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>
                                  {
                                    formatDateTime(entry.timestamp).split(
                                      ", ",
                                    )[1]
                                  }
                                </span>
                                <span className="font-medium text-gray-700">
                                  {formatUser(entry.userName)}
                                </span>
                              </div>
                            </div>

                            {/* Value Changes */}
                            {(entry.oldValue || entry.newValue) && (
                              <div className="mt-2 flex items-center gap-2 text-sm">
                                {entry.oldValue && (
                                  <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded line-through">
                                    {entry.oldValue.length > 50
                                      ? `${entry.oldValue.slice(0, 50)}...`
                                      : entry.oldValue}
                                  </span>
                                )}
                                {entry.oldValue && entry.newValue && (
                                  <svg
                                    className="w-4 h-4 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                                    />
                                  </svg>
                                )}
                                {entry.newValue && (
                                  <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded">
                                    {entry.newValue.length > 50
                                      ? `${entry.newValue.slice(0, 50)}...`
                                      : entry.newValue}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Expanded Details */}
                            {isExpanded && (
                              <div className="mt-3 pt-3 border-t border-gray-200 space-y-2 text-sm">
                                {humanizeNotes(entry.notes) && (
                                  <p className="text-gray-600">
                                    <strong>Notes:</strong> {humanizeNotes(entry.notes)}
                                  </p>
                                )}
                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                                  <div>Entity ID: {shortenId(entry.entityId)}</div>
                                  {entry.ipAddress && (
                                    <div>IP: {entry.ipAddress}</div>
                                  )}
                                  {entry.sessionId && (
                                    <div>
                                      Session: {entry.sessionId.slice(0, 8)}...
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-slate-100/80 to-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Entity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Field
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Changes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEntries.map((entry) => {
                  const actionConfig =
                    actionLabels[entry.action] || actionLabels["view"];
                  const colorClasses = getColorClasses(actionConfig.color);

                  return (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDateTime(entry.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${colorClasses.bg} ${colorClasses.text}`}
                        >
                          {actionConfig.icon}
                          {actionConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {entry.entityType === "CDD" ? "CDD Record" : entry.entityType}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {humanizeField(entry.field) || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatUser(entry.userName)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {entry.oldValue || entry.newValue ? (
                          <div className="max-w-xs truncate">
                            {entry.oldValue && (
                              <span className="text-red-600 line-through">
                                {entry.oldValue}
                              </span>
                            )}
                            {entry.oldValue && entry.newValue && " → "}
                            {entry.newValue && (
                              <span className="text-green-600">
                                {entry.newValue}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditTrail;
